import { merge } from "lodash";
import { AzureIcon } from "src/ui/icons";
import { AWSIcon, GCPIcon } from "src/ui/icons";

export enum HightouchRegionGroups {
  us = "us",
  eu = "eu",
  in = "in",
  me = "me",
}

export enum HightouchProviderGroups {
  aws = "aws",
  gcp = "gcp",
  azure = "azure",
}

export const regionGroupConfigs: Record<
  HightouchRegionGroups,
  {
    id: HightouchRegionGroups;
    label: string;
    emoji: string;
  }
> = {
  us: {
    id: HightouchRegionGroups.us,
    label: "United States",
    emoji: "🇺🇸",
  },
  eu: {
    id: HightouchRegionGroups.eu,
    label: "European Union",
    emoji: "🇪🇺",
  },
  in: {
    id: HightouchRegionGroups.in,
    label: "Asia Pacific",
    emoji: "🇮🇳🇦🇺",
  },
  me: {
    id: HightouchRegionGroups.me,
    label: "Middle East",
    emoji: "🇸🇦", // TODO: consider changing flag once there are other ME regions
  },
};

export const providerGroupConfigs: Record<
  HightouchProviderGroups,
  {
    label: string;
    logo: React.FC;
  }
> = {
  aws: { label: "AWS", logo: AWSIcon },
  gcp: { label: "Google Cloud", logo: GCPIcon },
  azure: { label: "Azure", logo: AzureIcon },
};

export interface HightouchRegionConfig {
  regionName: string;
  apiEndpoint: string;
  ipAddresses: string[];

  events: {
    enabled: boolean;
    endpoint: string;
  };

  tunnels: {
    enabled: boolean;
    reverseTunnelAddress: string;
    useCentralReverseSSHD: boolean;
  };

  /**
   * Mark a region as Beta, i.e. for internal use only.
   *  This is primarily used when bringing a new region online.
   */
  beta?: boolean;

  /**
   * Hide a region from the workspace-creation region picker entirely,
   * regardless of feature flags. Used for failover-only regions (e.g.
   * aws-us-west-2 for control-plane disaster recovery) that workspaces can be
   * moved into operationally but should never be created in directly.
   */
  hidden?: boolean;

  regionGroup: (typeof regionGroupConfigs)[keyof typeof regionGroupConfigs];
  providerGroup: (typeof providerGroupConfigs)[keyof typeof providerGroupConfigs];
}

export enum HightouchRegionOptions {
  AWS_US_EAST_1 = "aws-us-east-1",
  AWS_US_EAST_2 = "aws-us-east-2",
  AWS_US_WEST_2 = "aws-us-west-2",
  AWS_EU_WEST_1 = "aws-eu-west-1",
  AWS_AP_SOUTH_1 = "aws-ap-south-1",
  AWS_AP_SOUTHEAST_2 = "aws-ap-southeast-2",
  GCP_US_EAST_4 = "gcp-us-east4",
  GCP_EUROPE_WEST_1 = "gcp-europe-west1",
  GCP_ME_CENTRAL_2 = "gcp-me-central2",
  AZURE_EASTUS = "azure-eastus",
}

export const HightouchRegions: Record<string, HightouchRegionConfig> = {
  [HightouchRegionOptions.AWS_US_EAST_1]: {
    regionName: "aws-us-east-1",
    regionGroup: regionGroupConfigs.us,
    providerGroup: providerGroupConfigs.aws,

    apiEndpoint: import.meta.env.VITE_API_REGIONAL_URL_AWS_US_EAST_1 as string,
    ipAddresses: [
      "54.196.30.169",
      "52.72.201.213",
      "18.213.226.96",
      "3.224.126.197",
      "3.217.26.199",
    ],
    events: {
      enabled: true,
      endpoint: "us-east-1.hightouch-events.com",
    },
    tunnels: {
      enabled: true,
      reverseTunnelAddress: "tunnel.aws-us-east-1.hightouch.com",
      useCentralReverseSSHD: true,
    },
  },

  // Internal-only region (AWS Ohio). Gated behind the `multiRegionBetaRegions`
  // flag via `beta` so it is not offered to customers in the region picker.
  [HightouchRegionOptions.AWS_US_EAST_2]: {
    regionName: "aws-us-east-2",
    regionGroup: regionGroupConfigs.us,
    providerGroup: providerGroupConfigs.aws,

    apiEndpoint: import.meta.env.VITE_API_REGIONAL_URL_AWS_US_EAST_2 as string,
    ipAddresses: ["3.140.160.112", "3.151.76.160", "3.17.234.38"],
    events: {
      enabled: true,
      endpoint: "us-east-2.hightouch-events.com",
    },
    tunnels: {
      enabled: true,
      reverseTunnelAddress: "tunnel.aws-us-east-2.hightouch.com",
      useCentralReverseSSHD: true,
    },
    beta: true,
  },

  // Failover-only region for control-plane disaster recovery (Platinum
  // Resiliency). Workspaces are moved here operationally during a us-east-1
  // failover; it is never offered for workspace creation (`hidden`).
  [HightouchRegionOptions.AWS_US_WEST_2]: {
    regionName: "aws-us-west-2",
    regionGroup: regionGroupConfigs.us,
    providerGroup: providerGroupConfigs.aws,
    hidden: true,

    apiEndpoint: import.meta.env.VITE_API_REGIONAL_URL_AWS_US_WEST_2 as string,
    // No stable NAT egress IPs published for this region yet; failover
    // customers connect via PrivateLink rather than IP whitelisting.
    ipAddresses: [],
    events: {
      enabled: false,
      endpoint: "",
    },
    tunnels: {
      enabled: false,
      reverseTunnelAddress: "tunnel.aws-us-west-2.hightouch.com",
      useCentralReverseSSHD: true,
    },
  },

  [HightouchRegionOptions.GCP_US_EAST_4]: {
    regionName: "gcp-us-east4",
    regionGroup: regionGroupConfigs.us,
    providerGroup: providerGroupConfigs.gcp,
    apiEndpoint: import.meta.env.VITE_API_REGIONAL_URL_GCP_US_EAST4 as string,
    ipAddresses: ["34.145.155.148", "35.245.150.71", "35.245.58.91"],
    events: {
      enabled: false,
      endpoint: "us-east4.hightouch-events.com",
    },
    tunnels: {
      enabled: true,
      reverseTunnelAddress: "tunnel.gcp-us-east4.production.hightouch.com",
      useCentralReverseSSHD: true,
    },
  },

  [HightouchRegionOptions.AZURE_EASTUS]: {
    regionName: "azure-eastus",
    regionGroup: regionGroupConfigs.us,
    providerGroup: providerGroupConfigs.azure,
    apiEndpoint: import.meta.env.VITE_API_REGIONAL_URL_AZURE_EASTUS as string,
    ipAddresses: ["23.100.16.164/30"],
    events: {
      enabled: true,
      endpoint: "azure-eastus.hightouch-events.com",
    },
    tunnels: {
      enabled: false,
      reverseTunnelAddress: "tunnel.azure-eastus.hightouch.com",
      useCentralReverseSSHD: true,
    },
  },

  [HightouchRegionOptions.AWS_EU_WEST_1]: {
    regionName: "aws-eu-west-1",
    regionGroup: regionGroupConfigs.eu,
    providerGroup: providerGroupConfigs.aws,
    apiEndpoint: import.meta.env.VITE_API_REGIONAL_URL_AWS_EU_WEST_1 as string,
    ipAddresses: ["34.255.226.212", "52.16.84.199", "52.211.114.138"],
    events: {
      enabled: true,
      endpoint: "eu-west-1.hightouch-events.com",
    },
    tunnels: {
      enabled: true,
      reverseTunnelAddress: "tunnel.aws-eu-west-1.hightouch.com",
      useCentralReverseSSHD: true,
    },
  },

  [HightouchRegionOptions.GCP_EUROPE_WEST_1]: {
    regionName: "gcp-europe-west1",
    regionGroup: regionGroupConfigs.eu,
    providerGroup: providerGroupConfigs.gcp,
    apiEndpoint: import.meta.env
      .VITE_API_REGIONAL_URL_GCP_EUROPE_WEST1 as string,
    ipAddresses: ["35.241.218.183", "35.195.167.179", "104.199.61.185"],
    events: {
      enabled: false,
      endpoint: "europe-west1.hightouch-events.com",
    },
    tunnels: {
      enabled: true,
      reverseTunnelAddress: "tunnel.gcp-europe-west1.hightouch.com",
      useCentralReverseSSHD: true,
    },
  },

  [HightouchRegionOptions.GCP_ME_CENTRAL_2]: {
    regionName: "gcp-me-central2",
    regionGroup: regionGroupConfigs.me,
    providerGroup: providerGroupConfigs.gcp,
    apiEndpoint: import.meta.env
      .VITE_API_REGIONAL_URL_GCP_ME_CENTRAL2 as string,
    // GCP -> external IP addresses -> hightouch-production-nat-*-me-central2
    ipAddresses: ["34.166.244.44", "34.166.247.32", "34.166.115.20"],
    events: {
      enabled: false, // TODO not yet enabled in infra
      endpoint: "me-central2.hightouch-events.com",
    },
    tunnels: {
      enabled: false, // TODO not yet enabled in infra
      reverseTunnelAddress: "tunnel.gcp-me-central2.hightouch.com",
      useCentralReverseSSHD: true,
    },
  },

  [HightouchRegionOptions.AWS_AP_SOUTH_1]: {
    regionName: "aws-ap-south-1",
    regionGroup: regionGroupConfigs.in,
    providerGroup: providerGroupConfigs.aws,
    apiEndpoint: import.meta.env.VITE_API_REGIONAL_URL_AWS_AP_SOUTH_1 as string,
    ipAddresses: ["13.126.190.7", "65.1.90.146"],
    events: {
      enabled: true,
      endpoint: "ap-south-1.hightouch-events.com",
    },
    tunnels: {
      enabled: true,
      reverseTunnelAddress: "tunnel.aws-ap-south-1.hightouch.com",
      useCentralReverseSSHD: true,
    },
  },
  [HightouchRegionOptions.AWS_AP_SOUTHEAST_2]: {
    regionName: "aws-ap-southeast-2",
    regionGroup: regionGroupConfigs.in,
    providerGroup: providerGroupConfigs.aws,
    apiEndpoint: import.meta.env
      .VITE_API_REGIONAL_URL_AWS_AP_SOUTHEAST_2 as string,
    ipAddresses: ["13.238.99.151", "3.104.61.99", "13.54.27.62"],
    events: {
      enabled: true,
      endpoint: "ap-southeast-2.hightouch-events.com",
    },
    tunnels: {
      enabled: false,
      reverseTunnelAddress: "tunnel.aws-ap-southeast-2.hightouch.com",
      useCentralReverseSSHD: false,
    },
    beta: true,
  },
};

export function getHightouchRegionConfig(
  region: string,
): HightouchRegionConfig {
  const regionConfig = HightouchRegions[region];
  if (!regionConfig) {
    throw new Error("Invalid Hightouch region: " + region);
  }

  const workspaceRegionConfig = window?.Hightouch?.regionConfig;
  // Merge into a fresh object — `merge(regionConfig, ...)` would mutate the
  // static `HightouchRegions` entry, leaking a workspace's `region_config`
  // override into every subsequent lookup (including other regions').
  return merge({}, regionConfig, workspaceRegionConfig || {});
}

export const getRegionFullName = ({
  regionGroup,
  regionName,
}: Pick<HightouchRegionConfig, "regionName" | "regionGroup">) =>
  `${regionGroup.label} (${regionName})`;
